import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { db } from '../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';
import './Welfare.css';

const Welfare = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { showToast } = useToast();
    const [stats, setStats] = useState({
        semesterTotal: 0,
        lunchTotal: 0,
        helpTotal: 0
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadWelfareStats();
    }, []);

    const loadWelfareStats = async () => {
        setLoading(true);
        try {
            // Query contributions for semester subscription
            const semesterQuery = query(
                collection(db, 'contributions'),
                where('category', '==', 'sem_subscription')
            );
            const semesterSnapshot = await getDocs(semesterQuery);
            const semesterTotal = semesterSnapshot.docs.reduce((sum, doc) => {
                return sum + (doc.data().amount || 0);
            }, 0);

            // Query contributions for social lunch
            const lunchQuery = query(
                collection(db, 'contributions'),
                where('category', '==', 'social_lunch')
            );
            const lunchSnapshot = await getDocs(lunchQuery);
            const lunchTotal = lunchSnapshot.docs.reduce((sum, doc) => {
                return sum + (doc.data().amount || 0);
            }, 0);

            // Query contributions for help me fund (donations)
            const helpQuery = query(
                collection(db, 'contributions'),
                where('category', '==', 'donation')
            );
            const helpSnapshot = await getDocs(helpQuery);
            const helpTotal = helpSnapshot.docs.reduce((sum, doc) => {
                return sum + (doc.data().amount || 0);
            }, 0);

            setStats({
                semesterTotal,
                lunchTotal,
                helpTotal
            });
        } catch (error) {
            console.error('Error loading welfare stats:', error);
            showToast('Error loading statistics', 'error');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const handleSupport = (category) => {
        // Navigate to payment page with category pre-filled
        navigate(`/payment?category=${category}`);
    };

    const handleHelpMe = () => {
        if (!currentUser) {
            showToast('Please log in to request assistance', 'info');
            navigate('/login');
            return;
        }
        navigate('/help-me');
    };

    return (
        <div className="welfare-page">
            <header className="welfare-header">
                <div className="welfare-logo-container">
                    <h1 className="welfare-header-title"> Ministry - Welfare</h1>
                </div>
            </header>

            <h2 className="welfare-page-title">Support Our Ministry Welfare Programs</h2>

            <div className="welfare-cards-container">
                {/* Semester Subscription Card */}
                <div className="welfare-card">
                    <h3 className="welfare-card-title">Semester Subscription</h3>
                    <div className="welfare-card-price">Ksh. 100</div>
                    <div className="welfare-card-description">
                        <p>Your small but consistent contribution helps sustain our ministry's operational costs throughout each semester. This fund ensures we can continue our Bible study programs, ministry activities, and community outreach without interruption.</p>
                        <p>Even this small amount makes a big difference when we all participate together!</p>
                    </div>
                    <button 
                        className="welfare-btn welfare-btn-primary"
                        onClick={() => handleSupport('sem_subscription')}
                    >
                        <i className="fas fa-handshake"></i> Support
                    </button>
                </div>

                {/* Social Lunch Subscription Card */}
                <div className="welfare-card">
                    <h3 className="welfare-card-title">Social Lunch Subscription</h3>
                    <div className="welfare-card-price">Ksh. 400</div>
                    <div className="welfare-card-description">
                        <p>Our monthly social lunches foster fellowship and strengthen our community bonds. Your contribution helps provide meals for members after Sabbath services and creates opportunities for deeper connections among our church family.</p>
                        <p>This is more than just a meal - it's nourishment for both body and soul.</p>
                    </div>
                    <button 
                        className="welfare-btn welfare-btn-primary"
                        onClick={() => handleSupport('social_lunch')}
                    >
                        <i className="fas fa-handshake"></i> Support
                    </button>
                </div>

                {/* Help Me Card */}
                <div className="welfare-card">
                    <h3 className="welfare-card-title">Help Me</h3>
                    <div className="welfare-card-description">
                        <p>We recognize that some among us may be going through difficult times. As a ministry family, we are committed to supporting each other in times of need.</p>
                        <p>If you need assistance, don't hesitate to reach out. If you're able, please consider supporting this fund to help those in our community who are struggling.</p>
                    </div>
                    <div className="welfare-card-actions">
                        <button 
                            className="welfare-btn welfare-btn-success"
                            onClick={handleHelpMe}
                        >
                            <i className="fas fa-hands-helping"></i> Help Me
                        </button>
                        <button 
                            className="welfare-btn welfare-btn-primary"
                            onClick={() => handleSupport('donation')}
                        >
                            <i className="fas fa-handshake"></i> Support
                        </button>
                    </div>
                </div>
            </div>

            {/* Statistics Section */}
            <div className="welfare-stats-section">
                <h3 className="welfare-stats-title">Welfare Fund Statistics</h3>
                {loading ? (
                    <div className="welfare-stats-loading">
                        <i className="fas fa-spinner fa-spin"></i>
                        <p>Loading statistics...</p>
                    </div>
                ) : (
                    <div className="welfare-stats-grid">
                        <div className="welfare-stat-card">
                            <h4 className="welfare-stat-title">Semester Subscription</h4>
                            <div className="welfare-stat-value">
                                {formatCurrency(stats.semesterTotal)}
                            </div>
                            <div className="welfare-stat-description">
                                Total contributions this semester
                            </div>
                        </div>
                        
                        <div className="welfare-stat-card">
                            <h4 className="welfare-stat-title">Social Lunch</h4>
                            <div className="welfare-stat-value">
                                {formatCurrency(stats.lunchTotal)}
                            </div>
                            <div className="welfare-stat-description">
                                Raised for upcoming social lunch
                            </div>
                        </div>
                        
                        <div className="welfare-stat-card">
                            <h4 className="welfare-stat-title">Help Me Fund</h4>
                            <div className="welfare-stat-value">
                                {formatCurrency(stats.helpTotal)}
                            </div>
                            <div className="welfare-stat-description">
                                Available for members in need
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Bible Verse */}
            <div className="welfare-bible-verse">
                <p>"Brethren, do not grow weary in doing good."</p>
                <p>- 2 Thessalonians 3:13</p>
            </div>

            {/* WhatsApp Float */}
            <a 
                href="https://wa.me/+254114963959" 
                className="welfare-whatsapp-float" 
                target="_blank" 
                rel="noopener noreferrer"
            >
                <i className="fab fa-whatsapp"></i>
            </a>
        </div>
    );
};

export default Welfare;
